import { useState, useMemo } from 'react'
import { Card } from '@/components/ui/Card'
import { Tooltip } from '@/components/ui/Tooltip'
import { TrendingUp, TrendingDown, Minus, Target, Clock, Info, List, Hash, DollarSign, Copy, Check, FileText } from 'lucide-react'
import { usePresalesDashboardStore } from '../store'
import { KR1DealDetailModal } from './KR1DealDetail'
import { KR2DealDetailModal } from './KR2DealDetail'
import { formatCurrency } from '../utils/dealHelpers'
import { usePresalesMembers } from '../utils/taskHelpers'

type KR1ViewMode = 'count' | 'value'

const KR2_METHODOLOGY_TEXT = (baselineDays: number, historicalAvg: number) => `KR2: Avg Presales Cycle (Engagement to Close)

How it's calculated:
Avg Days = Close Date − Presales Start Date

Start date: The earliest of these three dates (whichever comes first):
1. SC Owner Assigned Date — when the Solutions Consultant was first assigned to the deal in HubSpot (pulled from HubSpot property history)
2. Presales Engagement Date — set by a HubSpot workflow when the Presales Engineer field is populated
3. First Demo Date — the earliest demo event linked to the deal in DealHub

End date: Deal Close Date (won deals only)

Why use the earliest date?
The HubSpot "Presales Engagement Date" is set by a workflow that fires when the Presales Engineer field is filled in. In practice, this field is often populated late (sometimes at or after close), making it unreliable. The SC Owner field is typically set when presales actually starts working the deal, so it's a better signal. First Demo Date catches cases where demos happened before either field was set.

Which deals are included?
Only closed-won deals where a Presales Engineer is assigned. Deals are attributed to the quarter their presales engagement started (not when they closed). This means a quarter's numbers can change after the quarter ends as late-closing deals get added.

How is performance measured?
Each quarter is compared against the previous quarter as a % reduction. Target is 15% reduction quarter-over-quarter.
- Historical avg (pre-2026): ${historicalAvg}d
- Q1 2026 baseline: ${baselineDays}d`

function KR2MethodologyTooltip({ baselineDays, historicalAvg }: { baselineDays: number; historicalAvg?: number }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(KR2_METHODOLOGY_TEXT(baselineDays, historicalAvg || 0))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Tooltip
      content={
        <div className="space-y-3 max-w-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="font-semibold text-sm">How is Presales Cycle calculated?</div>
            <button
              onClick={handleCopy}
              className="shrink-0 p-1 rounded hover:bg-gray-600/50 transition-colors"
              title="Copy methodology to clipboard"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-400" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-gray-400 hover:text-gray-200" />
              )}
            </button>
          </div>
          <div className="text-xs leading-relaxed">
            Avg Days = <span className="font-mono bg-gray-700/50 px-1 rounded">Close Date &minus; Presales Start Date</span>
          </div>

          <div className="border-t border-gray-700 pt-2">
            <div className="font-semibold text-xs mb-1">Start date (earliest of):</div>
            <div className="text-xs text-gray-400 leading-relaxed space-y-1">
              <div><strong>1. SC Owner Assigned</strong> &mdash; when the Solutions Consultant was first assigned in HubSpot (from property history)</div>
              <div><strong>2. Presales Engagement Date</strong> &mdash; set by HubSpot workflow when Presales Engineer is populated</div>
              <div><strong>3. First Demo Date</strong> &mdash; earliest demo event linked to the deal</div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-2">
            <div className="font-semibold text-xs mb-1">End date:</div>
            <div className="text-xs text-gray-400 leading-relaxed">
              Deal Close Date (won deals only)
            </div>
          </div>

          <div className="border-t border-gray-700 pt-2">
            <div className="font-semibold text-xs mb-1">Why use the earliest date?</div>
            <div className="text-xs text-gray-400 leading-relaxed">
              The HubSpot &ldquo;Presales Engagement Date&rdquo; is set by a workflow when the Presales Engineer field is filled in.
              This field is often populated late (sometimes at close), making it unreliable.
              The SC Owner field is typically set when presales starts working the deal, so it&rsquo;s a better signal.
            </div>
          </div>

          <div className="border-t border-gray-700 pt-2">
            <div className="font-semibold text-xs mb-1">How is performance measured?</div>
            <div className="text-xs text-gray-400 leading-relaxed">
              Each quarter is compared against the previous quarter as a % reduction. Target: 15% reduction QoQ.
              Deals are attributed by quarter their engagement started (not when they closed).
            </div>
          </div>

          <div className="text-xs text-gray-500">
            {historicalAvg ? `Historical avg (pre-2026): ${historicalAvg}d | ` : ''}Q1 2026 baseline: {baselineDays}d
          </div>
        </div>
      }
    >
      <Info className="h-3.5 w-3.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)] cursor-help transition-colors" />
    </Tooltip>
  )
}

export function NorthStarsSection() {
  const { kr1Rolling, kr2Rolling, isLoadingKRs } = usePresalesDashboardStore()
  const [showKR1Deals, setShowKR1Deals] = useState(false)
  const [showKR2Deals, setShowKR2Deals] = useState(false)
  const [kr1View, setKr1View] = useState<KR1ViewMode>('count')

  if (isLoadingKRs || !kr1Rolling || !kr2Rolling) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <Card key={i} className="p-6">
            <div className="space-y-3">
              <div className="h-4 bg-[var(--bg-secondary)] animate-pulse rounded w-32" />
              <div className="h-10 bg-[var(--bg-secondary)] animate-pulse rounded w-24" />
              <div className="h-3 bg-[var(--bg-secondary)] animate-pulse rounded w-48" />
            </div>
          </Card>
        ))}
      </div>
    )
  }

  const { ttm: kr1Ttm, rolling_6m: kr1_6m, all_time: kr1All } = kr1Rolling
  const { baseline_days, current_quarter: kr2CurrentQ, previous_quarter: kr2PrevQ, historical_avg: kr2HistoricalAvg, target_days: kr2TargetDays } = kr2Rolling

  // KR1: Win Ratio
  const kr1Target = 50
  const kr1AboveTarget = kr1Ttm.win_ratio >= kr1Target
  const kr1Color = kr1AboveTarget ? 'text-green-400' : kr1Ttm.win_ratio > 0 ? 'text-red-400' : 'text-[var(--text-muted)]'
  const kr1BgAccent = kr1AboveTarget ? 'border-green-700/30' : 'border-red-700/30'
  const KR1TrendIcon = kr1AboveTarget ? TrendingUp : kr1Ttm.win_ratio > 0 ? TrendingDown : Minus
  const kr1TrendColor = kr1AboveTarget ? 'text-green-400' : 'text-red-400'

  // Compare TTM to 6M to show direction
  const kr1Direction = kr1_6m.win_ratio > kr1Ttm.win_ratio ? 'improving' : kr1_6m.win_ratio < kr1Ttm.win_ratio ? 'declining' : 'stable'
  const kr1DirectionLabel =
    kr1Direction === 'improving'
      ? `Trending up (6M: ${kr1_6m.win_ratio.toFixed(0)}%)`
      : kr1Direction === 'declining'
      ? `Trending down (6M: ${kr1_6m.win_ratio.toFixed(0)}%)`
      : 'Stable'

  // KR1 value-based win ratio helpers
  const valueWinRatio = (m: { won_amount: number; lost_amount: number }) => {
    const closed = m.won_amount + m.lost_amount
    return closed > 0 ? (m.won_amount / closed) * 100 : 0
  }
  const kr1TtmValueRatio = valueWinRatio(kr1Ttm)
  const kr1AllValueRatio = valueWinRatio(kr1All)
  const kr1_6mValueRatio = valueWinRatio(kr1_6m)

  const kr1ValueAboveTarget = kr1TtmValueRatio >= kr1Target
  const kr1ValueColor = kr1ValueAboveTarget ? 'text-green-400' : kr1TtmValueRatio > 0 ? 'text-red-400' : 'text-[var(--text-muted)]'

  const kr1_6mValueDirection = kr1_6mValueRatio > kr1TtmValueRatio ? 'improving' : kr1_6mValueRatio < kr1TtmValueRatio ? 'declining' : 'stable'
  const kr1ValueDirectionLabel =
    kr1_6mValueDirection === 'improving'
      ? `Trending up (6M: ${kr1_6mValueRatio.toFixed(0)}%)`
      : kr1_6mValueDirection === 'declining'
      ? `Trending down (6M: ${kr1_6mValueRatio.toFixed(0)}%)`
      : 'Stable'

  // KR2: Presales Cycle — use current quarter data for the card
  const kr2CQ = kr2CurrentQ as { label: string; avg_days: number; deal_count: number; p50: number } | undefined
  const kr2PQ = kr2PrevQ as { label: string; avg_days: number; deal_count: number; p50: number } | undefined
  const kr2HasData = kr2CQ && kr2CQ.deal_count > 0
  const kr2CurrentAvg = kr2CQ?.avg_days || 0
  const kr2PrevAvg = kr2PQ?.avg_days || 0
  const kr2Historical = (kr2HistoricalAvg as number) || 0

  // Compare current quarter against previous quarter
  const kr2BelowPrev = kr2HasData && kr2PrevAvg > 0 && kr2CurrentAvg < kr2PrevAvg
  const kr2Color = !kr2HasData
    ? 'text-[var(--text-muted)]'
    : kr2BelowPrev
    ? 'text-green-400'
    : kr2CurrentAvg <= kr2PrevAvg * 1.15
    ? 'text-yellow-400'
    : 'text-red-400'
  const kr2BgAccent = !kr2HasData ? 'border-blue-700/30' : kr2BelowPrev ? 'border-green-700/30' : 'border-amber-700/30'
  const KR2TrendIcon = kr2BelowPrev ? TrendingDown : kr2CurrentAvg > kr2PrevAvg ? TrendingUp : Minus
  const kr2TrendColor = kr2BelowPrev ? 'text-green-400' : kr2CurrentAvg > kr2PrevAvg ? 'text-red-400' : 'text-[var(--text-muted)]'

  // QoQ % reduction
  const kr2QoQPercent = kr2PrevAvg > 0 ? ((kr2PrevAvg - kr2CurrentAvg) / kr2PrevAvg) * 100 : 0
  // vs historical % reduction (for Q1 which compares against pre-Q1 historical)
  const kr2VsHistPercent = kr2Historical > 0 && kr2PrevAvg > 0 ? ((kr2Historical - kr2PrevAvg) / kr2Historical) * 100 : 0

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          North Stars
        </h2>
        <Tooltip
          content={
            <div className="max-w-xs">
              <div className="font-semibold mb-1">Trailing 12-Month (TTM) metrics</div>
              <div className="text-xs">
                These are your primary KPIs. Rolling windows smooth out small-sample volatility
                and give a stable view of presales performance.
              </div>
            </div>
          }
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* KR1: Win Ratio */}
        <Card className={`overflow-hidden border ${kr1View === 'count' ? kr1BgAccent : kr1ValueAboveTarget ? 'border-green-700/30' : 'border-red-700/30'}`}>
          <div className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Target className="h-4 w-4 text-blue-400" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">KR1</span>
                  <span className="text-xs text-[var(--text-muted)]">Win Ratio (Strategic Deals)</span>
                  <Tooltip
                    content={
                      <div className="space-y-3 max-w-sm">
                        <div>
                          <div className="font-semibold text-sm mb-1">How is Win Ratio calculated?</div>
                          <div className="text-xs leading-relaxed">
                            {kr1View === 'count' ? (
                              <>Win Ratio = <span className="font-mono bg-gray-700/50 px-1 rounded">Won Deals ÷ (Won + Lost)</span></>
                            ) : (
                              <>Value Win Ratio = <span className="font-mono bg-gray-700/50 px-1 rounded">Won $ ÷ (Won $ + Lost $)</span></>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Only <strong>closed</strong> deals count. Open deals are excluded from the ratio but shown for context.
                          </div>
                        </div>

                        <div className="border-t border-gray-700 pt-2">
                          <div className="font-semibold text-xs mb-1">What do the numbers mean?</div>
                          <div className="space-y-1 text-xs">
                            <div><span className="text-green-400 font-medium">{kr1Ttm.won} Won</span> — deals closed successfully</div>
                            <div><span className="text-red-400 font-medium">{kr1Ttm.lost} Lost</span> — deals closed-lost</div>
                            <div><span className="text-gray-400 font-medium">{kr1Ttm.open} Open</span> — still in pipeline (not in ratio)</div>
                          </div>
                        </div>

                        <div className="border-t border-gray-700 pt-2">
                          <div className="font-semibold text-xs mb-1">Timeframe: TTM (Trailing 12 Months)</div>
                          <div className="text-xs text-gray-400 leading-relaxed">
                            Looks at all strategic deals with a <strong>presales engagement date</strong> in the
                            last 12 months. This rolling window smooths out quarter-to-quarter noise and gives
                            a stable picture of win performance.
                          </div>
                        </div>

                        <div className="border-t border-gray-700 pt-2">
                          <div className="font-semibold text-xs mb-1">Which deals are included?</div>
                          <div className="text-xs text-gray-400 leading-relaxed">
                            Strategic deals only (New Business, Upsell, Strategic) where presales was involved.
                            Deals are attributed by <strong>when presales engaged</strong>, not when they closed —
                            this credits the presales work at the right point in time.
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <Info className="h-3.5 w-3.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)] cursor-help transition-colors" />
                  </Tooltip>
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  {kr1View === 'count' ? (
                    <span className={`text-4xl font-bold ${kr1Color}`}>
                      {kr1Ttm.win_ratio > 0 ? `${kr1Ttm.win_ratio.toFixed(0)}%` : '--'}
                    </span>
                  ) : (
                    <span className={`text-4xl font-bold ${kr1ValueColor}`}>
                      {kr1TtmValueRatio > 0 ? `${kr1TtmValueRatio.toFixed(0)}%` : '--'}
                    </span>
                  )}
                  <span className="text-sm text-[var(--text-muted)]">TTM</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1">
                {/* Count / Value toggle */}
                <div className="flex rounded-md border border-[var(--border-color)] overflow-hidden">
                  <button
                    onClick={() => setKr1View('count')}
                    className={`px-2 py-1 text-xs font-medium transition-colors ${
                      kr1View === 'count'
                        ? 'bg-blue-500/20 text-blue-400 border-r border-[var(--border-color)]'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)] border-r border-[var(--border-color)]'
                    }`}
                    title="Deal count view"
                  >
                    <Hash className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setKr1View('value')}
                    className={`px-2 py-1 text-xs font-medium transition-colors ${
                      kr1View === 'value'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                    }`}
                    title="Deal value view"
                  >
                    <DollarSign className="h-3.5 w-3.5" />
                  </button>
                </div>
                {kr1Ttm.win_ratio > 0 && (
                  <div className={`flex items-center gap-1 ${kr1View === 'count' ? kr1TrendColor : kr1ValueAboveTarget ? 'text-green-400' : 'text-red-400'}`}>
                    {kr1View === 'count' ? (
                      <KR1TrendIcon className="h-4 w-4" />
                    ) : kr1ValueAboveTarget ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <TrendingDown className="h-4 w-4" />
                    )}
                  </div>
                )}
              </div>
            </div>

            {kr1View === 'count' ? (
              <>
                <div className="mt-3 flex items-center gap-4 text-xs">
                  <span className="text-[var(--text-muted)]">
                    Target: <span className="text-[var(--text-secondary)]">&gt;50%</span>
                  </span>
                  <span className="text-[var(--text-muted)]">|</span>
                  <span className="text-green-400">{kr1Ttm.won} Won</span>
                  <span className="text-red-400">{kr1Ttm.lost} Lost</span>
                  <span className="text-[var(--text-muted)]">{kr1Ttm.open} Open</span>
                </div>

                <div className="mt-2 flex items-center gap-3 text-xs text-[var(--text-muted)]">
                  <span>All Time: <span className="text-[var(--text-secondary)]">{kr1All.win_ratio.toFixed(0)}%</span></span>
                  <span>6M: <span className="text-[var(--text-secondary)]">{kr1_6m.win_ratio.toFixed(0)}%</span></span>
                  {kr1Direction !== 'stable' && (
                    <span className={kr1Direction === 'improving' ? 'text-green-400' : 'text-red-400'}>
                      {kr1DirectionLabel}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="mt-3 flex items-center gap-4 text-xs">
                  <span className="text-[var(--text-muted)]">
                    Target: <span className="text-[var(--text-secondary)]">&gt;50%</span>
                  </span>
                  <span className="text-[var(--text-muted)]">|</span>
                  <span className="text-green-400">{formatCurrency(kr1Ttm.won_amount)} Won</span>
                  <span className="text-red-400">{formatCurrency(kr1Ttm.lost_amount)} Lost</span>
                  <span className="text-[var(--text-muted)]">{formatCurrency(kr1Ttm.open_amount)} Open</span>
                </div>

                <div className="mt-2 flex items-center gap-3 text-xs text-[var(--text-muted)]">
                  <span>All Time: <span className="text-[var(--text-secondary)]">{kr1AllValueRatio > 0 ? `${kr1AllValueRatio.toFixed(0)}%` : '--'}</span></span>
                  <span>6M: <span className="text-[var(--text-secondary)]">{kr1_6mValueRatio > 0 ? `${kr1_6mValueRatio.toFixed(0)}%` : '--'}</span></span>
                  {kr1_6mValueDirection !== 'stable' && (
                    <span className={kr1_6mValueDirection === 'improving' ? 'text-green-400' : 'text-red-400'}>
                      {kr1ValueDirectionLabel}
                    </span>
                  )}
                </div>
              </>
            )}

            <button
              onClick={() => setShowKR1Deals(true)}
              className="mt-3 flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              <List className="h-3 w-3" />
              View Deals
            </button>
          </div>
        </Card>

        {/* KR2: Avg Presales Cycle (engaged → close) */}
        <Card className={`overflow-hidden border ${kr2BgAccent}`}>
          <div className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-blue-400" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">KR2</span>
                  <span className="text-xs text-[var(--text-muted)]">Avg Presales Cycle (Engaged &rarr; Close)</span>
                  <KR2MethodologyTooltip baselineDays={baseline_days} historicalAvg={kr2Historical} />
                </div>
                {kr2HasData ? (
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className={`text-4xl font-bold ${kr2Color}`}>
                      {kr2CurrentAvg}d
                    </span>
                    <span className="text-sm text-[var(--text-muted)]">{kr2CQ?.label}</span>
                  </div>
                ) : (
                  <div className="mt-2">
                    <span className="text-2xl font-bold text-[var(--text-muted)]">Awaiting data</span>
                    <div className="text-xs text-[var(--text-muted)] mt-1">
                      No closed deals with presales engagement this quarter yet
                    </div>
                  </div>
                )}
              </div>
              {kr2HasData && (
                <div className={`flex items-center gap-1 mt-2 ${kr2TrendColor}`}>
                  <KR2TrendIcon className="h-4 w-4" />
                </div>
              )}
            </div>

            {kr2HasData ? (
              <>
                {/* QoQ reduction */}
                <div className="mt-3 flex items-center gap-2 text-xs">
                  {kr2PrevAvg > 0 && (
                    <span className={`font-semibold ${kr2QoQPercent > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {kr2QoQPercent > 0 ? `${kr2QoQPercent.toFixed(0)}% reduction` : `${Math.abs(kr2QoQPercent).toFixed(0)}% increase`} from {kr2PQ?.label}
                    </span>
                  )}
                </div>

                {/* Stats row */}
                <div className="mt-2 flex items-center gap-4 text-xs">
                  <span className="text-[var(--text-muted)]">
                    {kr2PQ?.label}: <span className="text-[var(--text-secondary)]">{kr2PrevAvg}d</span>
                    {kr2PQ?.deal_count ? <span className="text-[var(--text-muted)]"> ({kr2PQ.deal_count} deals)</span> : null}
                  </span>
                  <span className="text-[var(--text-muted)]">|</span>
                  <span className="text-[var(--text-muted)]">
                    Target: <span className="text-[var(--text-secondary)]">{kr2TargetDays}d</span> <span className="text-[var(--text-muted)]">(-15%)</span>
                  </span>
                </div>

                {/* Current quarter details */}
                <div className="mt-2 flex items-center gap-3 text-xs text-[var(--text-muted)]">
                  <span>{kr2CQ?.deal_count} deal{kr2CQ?.deal_count !== 1 ? 's' : ''}</span>
                  <span>P50: <span className="text-[var(--text-secondary)]">{kr2CQ?.p50}d</span></span>
                  {kr2Historical > 0 && kr2VsHistPercent > 0 && (
                    <span className="text-[var(--text-muted)]">
                      Historical avg: <span className="text-[var(--text-secondary)]">{kr2Historical}d</span>
                    </span>
                  )}
                </div>

                <button
                  onClick={() => setShowKR2Deals(true)}
                  className="mt-3 flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <List className="h-3 w-3" />
                  View Deals
                </button>
              </>
            ) : (
              <div className="mt-3 space-y-1">
                {kr2PrevAvg > 0 && (
                  <div className="text-xs text-[var(--text-muted)]">
                    {kr2PQ?.label}: <span className="text-[var(--text-secondary)]">{kr2PrevAvg}d avg</span>
                    <span className="text-[var(--text-muted)]"> ({kr2PQ?.deal_count} deals)</span>
                  </div>
                )}
                {kr2Historical > 0 && (
                  <div className="text-xs text-[var(--text-muted)]">
                    Historical avg: <span className="text-[var(--text-secondary)]">{kr2Historical}d</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      <KR1DealDetailModal isOpen={showKR1Deals} onClose={() => setShowKR1Deals(false)} />
      <KR2DealDetailModal isOpen={showKR2Deals} onClose={() => setShowKR2Deals(false)} />

      {/* Blueprint Adoption — separated from KR cards */}
      <div className="mt-4">
        <BlueprintAdoptionCard />
      </div>
    </div>
  )
}

function BlueprintAdoptionCard() {
  const { deals } = usePresalesDashboardStore()
  const presalesMembers = usePresalesMembers()

  const stats = useMemo(() => {
    // Only count open deals with a presales engineer assigned on or after May 1 2026 (blueprint is a new process)
    const BLUEPRINT_CUTOFF = '2026-05-01'
    const openDeals = deals.filter((d) => !d.is_closed && d.presales_engineer && (d.presales_assigned_date || d.presales_engagement_date || '') >= BLUEPRINT_CUTOFF)

    const byMember: Record<string, { total: number; withBlueprint: number; avgPercent: number; dealsWith: string[]; dealsWithout: string[] }> = {}
    for (const name of presalesMembers) {
      const memberDeals = openDeals.filter((d) => d.presales_engineer === name)
      const withBp = memberDeals.filter((d) => (d.blueprint_completion_percent ?? 0) > 0)
      const withoutBp = memberDeals.filter((d) => (d.blueprint_completion_percent ?? 0) === 0)
      const totalPercent = memberDeals.reduce((sum, d) => sum + (d.blueprint_completion_percent ?? 0), 0)
      byMember[name] = {
        total: memberDeals.length,
        withBlueprint: withBp.length,
        avgPercent: memberDeals.length > 0 ? Math.round(totalPercent / memberDeals.length) : 0,
        dealsWith: withBp.map((d) => `${d.deal_name} (${d.blueprint_completion_percent}%)`),
        dealsWithout: withoutBp.map((d) => d.deal_name),
      }
    }

    const totalOpen = openDeals.length
    const totalWithBp = openDeals.filter((d) => (d.blueprint_completion_percent ?? 0) > 0).length
    const overallPercent = totalOpen > 0 ? Math.round((totalWithBp / totalOpen) * 100) : 0

    return { byMember, totalOpen, totalWithBp, overallPercent }
  }, [deals, presalesMembers])

  const getFirstName = (name: string) => name.split(' ')[0]

  return (
    <Card className="overflow-hidden border border-violet-700/30">
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-4 w-4 text-violet-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-violet-400">Blueprint Adoption</span>
              <Tooltip content={
                <div className="space-y-1 max-w-xs">
                  <div className="font-semibold text-sm">Blueprint = quality presales</div>
                  <div className="text-xs text-gray-400">
                    Deals with a blueprint (&gt;0% complete) have a documented architectural hypothesis,
                    pain points, and stakeholder map before scoping begins. This is the first step in every engagement.
                  </div>
                </div>
              }>
                <Info className="h-3.5 w-3.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)] cursor-help transition-colors" />
              </Tooltip>
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <span className={`text-4xl font-bold ${stats.overallPercent >= 75 ? 'text-green-400' : stats.overallPercent >= 50 ? 'text-violet-400' : 'text-amber-400'}`}>
                {stats.overallPercent}%
              </span>
              <span className="text-sm text-[var(--text-muted)]">of open deals</span>
            </div>
          </div>
          <div className="text-right mt-1">
            <div className="text-2xl font-bold text-violet-400">{stats.totalWithBp}<span className="text-base text-[var(--text-muted)]">/{stats.totalOpen}</span></div>
            <div className="text-[10px] text-[var(--text-muted)]">deals with blueprint</div>
          </div>
        </div>

        {/* Per-member breakdown */}
        <div className="mt-4 space-y-3">
          {presalesMembers.map((name) => {
            const m = stats.byMember[name]
            if (!m) return null
            const pct = m.total > 0 ? Math.round((m.withBlueprint / m.total) * 100) : 0
            return (
              <div key={name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-[var(--text-primary)]">{getFirstName(name)}</span>
                  <Tooltip content={
                    m.total > 0 ? (
                      <div className="space-y-2 max-w-xs">
                        {m.dealsWith.length > 0 && (
                          <div>
                            <div className="font-semibold text-xs text-green-400 mb-1">With Blueprint ({m.dealsWith.length})</div>
                            {m.dealsWith.map((d, i) => (
                              <div key={i} className="text-xs text-gray-300 truncate">{d}</div>
                            ))}
                          </div>
                        )}
                        {m.dealsWithout.length > 0 && (
                          <div>
                            <div className="font-semibold text-xs text-red-400 mb-1">No Blueprint ({m.dealsWithout.length})</div>
                            {m.dealsWithout.map((d, i) => (
                              <div key={i} className="text-xs text-gray-300 truncate">{d}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : 'No open deals'
                  }>
                    <span className="text-xs text-[var(--text-secondary)] cursor-help border-b border-dotted border-[var(--text-muted)]">
                      {m.withBlueprint}/{m.total} deals
                      <span className="text-[var(--text-muted)]"> · </span>
                      <span className={pct >= 75 ? 'text-green-400' : pct >= 50 ? 'text-violet-400' : 'text-amber-400'}>{pct}%</span>
                      <span className="text-[var(--text-muted)]"> · avg {m.avgPercent}%</span>
                    </span>
                  </Tooltip>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all rounded-full ${pct >= 75 ? 'bg-green-500' : pct >= 50 ? 'bg-violet-500' : pct > 0 ? 'bg-violet-400' : ''}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Card>
  )
}
